import { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { renderAsync as renderDocx } from "docx-preview";
import * as XLSX from "xlsx";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// PDF.js worker:
// - Must match the API version used by react-pdf (pdfjs.version).
// - Avoid invalid deep imports into react-pdf due to package "exports" rules.
// - Prefer HTTPS to avoid mixed-content failures.
try {
  const v = String(pdfjs?.version || "").trim();
  if (v) {
    pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${v}/build/pdf.worker.min.mjs`;
  }
} catch {}

// Fallback: locally bundled worker (requires app-level pdfjs-dist to match pdfjs.version).
try {
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      "pdfjs-dist/build/pdf.worker.min.mjs",
      import.meta.url,
    ).toString();
  }
} catch {
  // Best-effort fallback (react-pdf may still attempt fake worker).
}

const normalizeExt = (nameOrUrl = "") => {
  const raw = String(nameOrUrl || "");
  const base = raw.split("?")[0].split("#")[0];
  const last = base.split("/").pop() || "";
  const ext = last.includes(".") ? last.split(".").pop() : "";
  return String(ext || "").toLowerCase().trim();
};

const inferPreviewKind = (file = {}) => {
  const explicit =
    typeof file.kind === "string" ? file.kind.trim().toLowerCase() : "";
  if (explicit) return explicit;

  const mime = String(file.mimeType || file.mime || "").toLowerCase();
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime === "application/pdf") return "pdf";

  const ext = normalizeExt(file.name || file.filename || file.url || "");
  if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)) {
    return "image";
  }
  if (["mp4", "webm", "mov", "mkv", "avi"].includes(ext)) return "video";
  if (["mp3", "wav", "flac", "aac", "m4a", "ogg"].includes(ext)) return "audio";
  if (ext === "pdf") return "pdf";
  if (ext === "doc") return "doc";
  if (ext === "docx") return "docx";
  if (ext === "xls") return "xls";
  if (ext === "xlsx") return "xlsx";
  if (ext === "txt") return "text";
  if (ext === "ppt") return "ppt";
  if (ext === "pptx") return "pptx";
  return "unknown";
};

const isPreviewDebugEnabled = () => {
  try {
    // Enable detailed logs in production by setting:
    // localStorage.setItem("preview_debug", "1")
    if (localStorage.getItem("preview_debug") === "1") return true;
  } catch {}
  return String(process.env.NODE_ENV || "").toLowerCase() !== "production";
};

const debugLog = (...args) => {
  if (!isPreviewDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log(...args);
};

const isProbablySafari = () => {
  try {
    const ua = String(navigator?.userAgent || "");
    // Safari includes "Safari" but not "Chrome"/"Chromium"/"Edg"/"OPR".
    const isSafari =
      /Safari\//.test(ua) &&
      !/Chrome\//.test(ua) &&
      !/Chromium\//.test(ua) &&
      !/Edg\//.test(ua) &&
      !/OPR\//.test(ua);
    return isSafari;
  } catch {
    return false;
  }
};

const normalizeToAbsoluteUrl = (inputUrl) => {
  const raw = String(inputUrl || "").trim();
  if (!raw) return null;
  try {
    const base =
      typeof window !== "undefined" && window.location
        ? window.location.href
        : "http://localhost/";
    return new URL(raw, base).toString();
  } catch {
    return null;
  }
};

const isLocalHostname = (hostname = "") => {
  const host = String(hostname || "").toLowerCase().trim();
  return host === "localhost" || host === "127.0.0.1" || host === "::1";
};

const inferOfficeType = (file = {}, kind = "") => {
  const k = String(kind || "").toLowerCase().trim();
  if (k === "doc" || k === "docx") return "word";
  if (k === "xls" || k === "xlsx") return "excel";
  if (k === "ppt" || k === "pptx") return "powerpoint";

  const mime = String(file?.mimeType || file?.mime || "").toLowerCase();
  if (mime.includes("word")) return "word";
  if (mime.includes("excel") || mime.includes("spreadsheet")) return "excel";
  if (mime.includes("powerpoint") || mime.includes("presentation")) return "powerpoint";
  return null;
};

const analyzeOfficeUrl = (inputUrl) => {
  const original = typeof inputUrl === "string" ? inputUrl : "";
  const absolute = normalizeToAbsoluteUrl(original);
  const safari = isProbablySafari();

  const out = {
    original,
    absolute,
    isBlob: Boolean(absolute && absolute.startsWith("blob:")),
    isHttps: false,
    isSameOrigin: false,
    isLocalhost: false,
    hasUploadsPath: false,
    hasApiPath: false,
    isSafari: safari,
    compatible: false,
    reason: "unknown",
  };

  if (!absolute) {
    out.reason = "missing_or_invalid_url";
    return out;
  }

  if (out.isBlob) {
    out.reason = "blob_url";
    return out;
  }

  let parsed = null;
  try {
    parsed = new URL(absolute);
  } catch {
    out.reason = "invalid_url";
    return out;
  }

  out.isHttps = parsed.protocol === "https:";
  out.isLocalhost = isLocalHostname(parsed.hostname);

  const origin =
    typeof window !== "undefined" && window.location ? window.location.origin : "";
  out.isSameOrigin = Boolean(origin) && parsed.origin === origin;

  const path = String(parsed.pathname || "");
  out.hasUploadsPath = path.includes("/uploads/");
  out.hasApiPath = path.includes("/api/");

  if (out.isLocalhost) {
    out.reason = "localhost";
    return out;
  }
  if (!out.isHttps) {
    out.reason = "non_https";
    return out;
  }

  // Best-effort gating: Office Online viewer needs a publicly reachable HTTPS URL.
  // API endpoints are often auth-protected and not reachable by Office Online.
  const isPublicFileApi =
    path.includes("/api/uploaded-files/viewSources") ||
    path.includes("/api/uploaded-files/viewSourcesPreviewPdf") ||
    path.includes("/api/uploaded-files/view");

  if (out.hasApiPath && !isPublicFileApi) {
    out.reason = "api_url";
    return out;
  }

  // Same-origin URLs are only considered safe when they look like static uploads.
  if (out.isSameOrigin && !out.hasUploadsPath) {
    out.reason = "same_origin_non_uploads";
    return out;
  }

  out.compatible = true;
  out.reason = out.isSameOrigin ? "same_origin_uploads" : "https_cross_origin";
  return out;
};

const buildOfficeViewerUrl = (absoluteFileUrl) => {
  const abs = String(absoluteFileUrl || "").trim();
  if (!abs) return "";
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
    abs,
  )}`;
};

const buildPptPreviewPdfUrlFromViewSources = (inputUrl) => {
  const abs = normalizeToAbsoluteUrl(inputUrl);
  if (!abs) return null;
  try {
    const u = new URL(abs);
    if (u.pathname.endsWith("/api/uploaded-files/viewSources")) {
      u.pathname = u.pathname.replace(
        /\/api\/uploaded-files\/viewSources$/,
        "/api/uploaded-files/viewSourcesPreviewPdf",
      );
      return u.toString();
    }
  } catch {}
  return null;
};

const ensureUrlForBlob = (blob) => {
  if (!blob) return null;
  try {
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
};

const fetchArrayBuffer = async (url, signal) => {
  const resp = await fetch(url, { signal });
  if (!resp.ok) throw new Error("Failed to fetch preview file");
  return await resp.arrayBuffer();
};

const withPdfViewerHints = (src) => {
  const url = String(src || "");
  if (!url) return url;
  // Some embedded PDF viewers honor these (not guaranteed in Chrome).
  const hints = "toolbar=0&navpanes=0&scrollbar=0";
  if (url.includes("#")) return `${url}&${hints}`;
  return `${url}#${hints}`;
};

const FilePreviewContent = ({ file, maxPdfWidth = 900 }) => {
  const docxRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [xlsxHtml, setXlsxHtml] = useState("");
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [blobUrl, setBlobUrl] = useState(null);
  const [docxBuffer, setDocxBuffer] = useState(null);
  const [xlsxBuffer, setXlsxBuffer] = useState(null);
  const [pdfWidth, setPdfWidth] = useState(null);
  const [pdfNativeFailed, setPdfNativeFailed] = useState(false);
  const [officeFailed, setOfficeFailed] = useState(false);
  const [officeFailReason, setOfficeFailReason] = useState("");
  const [officePreflight, setOfficePreflight] = useState({
    state: "idle", // idle | checking | ok | fail
    reason: "",
  });
  const docxFitRef = useRef({ lastScale: 1, lastWidth: 0, lastPageWidth: 0 });
  const docxFitRafRef = useRef(null);

  const kind = useMemo(() => inferPreviewKind(file || {}), [file]);
  const effectiveUrl = useMemo(() => {
    if (!file) return null;
    if (typeof file.url === "string" && file.url.trim()) return file.url.trim();
    return null;
  }, [file]);
  const officeType = useMemo(() => inferOfficeType(file || {}, kind), [file, kind]);
  const officeUrlAnalysis = useMemo(
    () => analyzeOfficeUrl(effectiveUrl),
    [effectiveUrl],
  );
  const officeIframeSrc = useMemo(
    () => (officeUrlAnalysis.compatible ? buildOfficeViewerUrl(officeUrlAnalysis.absolute) : ""),
    [officeUrlAnalysis],
  );
  const pptPreviewPdfUrl = useMemo(() => {
    if (kind !== "ppt" && kind !== "pptx") return null;
    return buildPptPreviewPdfUrlFromViewSources(effectiveUrl);
  }, [kind, effectiveUrl]);

  useEffect(() => {
    // Reset per-file view state when the file changes.
    setNumPages(null);
    setPdfWidth(null);
    setPdfNativeFailed(false);
    setStatus({ state: "idle", message: "" });
    setOfficeFailed(false);
    setOfficeFailReason("");
    setOfficePreflight({ state: "idle", reason: "" });
    try {
      if (docxFitRafRef.current) cancelAnimationFrame(docxFitRafRef.current);
    } catch {}
    docxFitRafRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.url, file?.name, file?.filename, file?.blob]);

  useEffect(() => {
    try {
      // eslint-disable-next-line no-console
      console.log("[Preview] Browser", {
        isSafari: isProbablySafari(),
        userAgent: navigator?.userAgent,
      });
    } catch {}
  }, []);

  useEffect(() => {
    if (!file) return;
    debugLog("[Preview] file info", {
      name: file?.name,
      url: file?.url,
      kind: file?.kind,
      mimeType: file?.mimeType || file?.mime,
      inferredKind: kind,
      ext: normalizeExt(file?.name || file?.filename || file?.url || ""),
      hasBlob: Boolean(file?.blob),
    });
  }, [file, kind]);

  useEffect(() => {
    if (!officeType) return;
    debugLog("[OfficePreview] url analysis", {
      officeType,
      ...officeUrlAnalysis,
    });
    if (!officeUrlAnalysis.compatible) {
      // eslint-disable-next-line no-console
      console.info("[OfficePreview] skipped", {
        officeType,
        reason: officeUrlAnalysis.reason,
        url: officeUrlAnalysis.original,
      });
    } else {
      debugLog("[OfficePreview] iframe src", officeIframeSrc);
    }
  }, [officeType, officeUrlAnalysis, officeIframeSrc]);

  // Preflight: avoid showing Office Online error pages by only attempting the viewer when
  // the file URL is likely reachable by a third-party service (Office Online).
  useEffect(() => {
    if (!officeType) return;
    if (!officeUrlAnalysis.compatible) return;
    if (!officeUrlAnalysis.absolute) return;

    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      setOfficePreflight({ state: "checking", reason: "" });
      debugLog("[OfficePreview] preflight start", {
        officeType,
        url: officeUrlAnalysis.absolute,
      });

      // Same-origin uploads: we can validate reachability reliably.
      // Cross-origin: fetch may fail due to CORS even if Office Online can access it,
      // so we keep the check best-effort and only fail hard on same-origin.
      const mustSucceed = Boolean(officeUrlAnalysis.isSameOrigin);

      try {
        const resp = await fetch(officeUrlAnalysis.absolute, {
          method: "HEAD",
          signal: controller.signal,
          cache: "no-store",
          credentials: "include",
        });

        if (cancelled) return;

        if (!resp.ok) {
          const reason = `head_status_${resp.status}`;
          debugLog("[OfficePreview] preflight fail", { officeType, reason });
          if (mustSucceed) {
            setOfficePreflight({ state: "fail", reason });
            setOfficeFailed(true);
            setOfficeFailReason(reason);
          } else {
            setOfficePreflight({ state: "ok", reason: "cors_unknown" });
          }
          return;
        }

        setOfficePreflight({ state: "ok", reason: "head_ok" });
        debugLog("[OfficePreview] preflight ok", { officeType });
      } catch (err) {
        if (cancelled) return;
        const name = String(err?.name || "error");
        debugLog("[OfficePreview] preflight error", { officeType, name });
        if (mustSucceed) {
          setOfficePreflight({ state: "fail", reason: name });
          setOfficeFailed(true);
          setOfficeFailReason(name);
        } else {
          // Cross-origin: don't block Office viewer solely due to CORS/network limitations.
          setOfficePreflight({ state: "ok", reason: "cors_unknown" });
        }
      }
    };

    run();
    return () => {
      cancelled = true;
      try {
        controller.abort();
      } catch {}
    };
  }, [officeType, officeUrlAnalysis.compatible, officeUrlAnalysis.absolute, officeUrlAnalysis.isSameOrigin]);

  useEffect(() => {
    if (!file?.blob) return;
    const created = ensureUrlForBlob(file.blob);
    if (!created) return;
    setBlobUrl(created);
    return () => {
      try {
        URL.revokeObjectURL(created);
      } catch {}
    };
  }, [file?.blob]);

  // DOCX: needs a Blob/ArrayBuffer.
  useEffect(() => {
    const officeAttempted =
      officeType === "word" && officeUrlAnalysis.compatible && !officeFailed;
    if (!file || kind !== "docx" || officeAttempted) return;
    if (!docxRef.current) return;

    let cancelled = false;
    const controller = new AbortController();

    const fitDocxToPanel = () => {
      const host = docxRef.current;
      if (!host) return;

      const wrapper =
        host.querySelector(".docx-wrapper") || host.firstElementChild;
      const section = host.querySelector("section");
      if (!wrapper || !section) return;

      const panelWidth = Math.max(0, Math.floor(host.clientWidth || 0));
      // Give a small safety margin so we don't clip due to rounding/shadows.
      const availableWidth = Math.max(0, panelWidth - 16);
      const pageWidth = Math.max(
        0,
        Math.floor(section.scrollWidth || section.offsetWidth || 0),
      );
      if (!availableWidth || !pageWidth) return;

      const fitScale = Math.min(1, availableWidth / pageWidth);
      const combined = Math.max(0.2, Math.min(4, fitScale));
      const prev = docxFitRef.current;
      if (
        Math.abs((prev.lastScale || 1) - combined) < 0.01 &&
        prev.lastWidth === availableWidth &&
        prev.lastPageWidth === pageWidth
      ) {
        return;
      }
      docxFitRef.current = {
        lastScale: combined,
        lastWidth: availableWidth,
        lastPageWidth: pageWidth,
      };

      // Prefer `zoom` because it affects layout (prevents horizontal overflow).
      // Fallback to `transform` if needed (paired with overflow-x hidden in CSS).
      try {
        wrapper.style.transform = "";
        wrapper.style.transformOrigin = "";
        wrapper.style.zoom = "";
        wrapper.style.marginLeft = "";
        wrapper.style.marginRight = "";
      } catch {}

      const scaleText = String(combined);
      let usedZoom = false;
      try {
        // eslint-disable-next-line no-param-reassign
        wrapper.style.zoom = scaleText;
        usedZoom = Boolean(wrapper.style.zoom);
      } catch {
        usedZoom = false;
      }

      if (!usedZoom) {
        try {
          wrapper.style.transformOrigin = "top center";
          wrapper.style.transform = `scale(${combined})`;
          wrapper.style.marginLeft = "auto";
          wrapper.style.marginRight = "auto";
        } catch {}
      }
    };

    const scheduleFit = () => {
      try {
        if (docxFitRafRef.current) cancelAnimationFrame(docxFitRafRef.current);
      } catch {}
      docxFitRafRef.current = requestAnimationFrame(() => {
        docxFitRafRef.current = null;
        fitDocxToPanel();
      });
    };

    const run = async () => {
      setStatus({ state: "loading", message: "Loading document..." });
      try {
        docxRef.current.innerHTML = "";

        const input = file.blob || docxBuffer;
        if (input) {
          await renderDocx(input, docxRef.current);
          scheduleFit();
          setTimeout(scheduleFit, 0);
          if (!cancelled) setStatus({ state: "ready", message: "" });
          return;
        }

        if (!effectiveUrl) {
          setStatus({ state: "error", message: "No preview source available." });
          return;
        }

        const buf = await fetchArrayBuffer(effectiveUrl, controller.signal);
        if (cancelled) return;
        setDocxBuffer(buf);
        await renderDocx(buf, docxRef.current);
        scheduleFit();
        setTimeout(scheduleFit, 0);
        if (!cancelled) setStatus({ state: "ready", message: "" });
      } catch (err) {
        if (!cancelled) {
          setStatus({
            state: "error",
            message: err?.message || "Failed to render document.",
          });
        }
      }
    };

    run();

    // Keep DOCX preview fitted when the viewport/panel resizes.
    // Note: Avoid ResizeObserver here; it can loop when we apply `zoom`.
    window.addEventListener("resize", scheduleFit);

    return () => {
      cancelled = true;
      try {
        controller.abort();
      } catch {}
      try {
        window.removeEventListener("resize", scheduleFit);
      } catch {}
      try {
        if (docxFitRafRef.current) cancelAnimationFrame(docxFitRafRef.current);
      } catch {}
      docxFitRafRef.current = null;
    };
  }, [file, kind, effectiveUrl, docxBuffer]);

  // XLSX: render HTML table.
  useEffect(() => {
    const officeAttempted =
      officeType === "excel" && officeUrlAnalysis.compatible && !officeFailed;
    if (!file || kind !== "xlsx" || officeAttempted) return;

    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      setStatus({ state: "loading", message: "Loading spreadsheet..." });
      try {
        const buf = file.blob
          ? await file.blob.arrayBuffer()
          : xlsxBuffer
            ? xlsxBuffer
            : effectiveUrl
              ? await fetchArrayBuffer(effectiveUrl, controller.signal)
              : null;

        if (!buf) {
          setStatus({ state: "error", message: "No preview source available." });
          return;
        }

        if (!cancelled && !file.blob && !xlsxBuffer) setXlsxBuffer(buf);

        const workbook = XLSX.read(buf, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const html = XLSX.utils.sheet_to_html(sheet);

        if (!cancelled) {
          setXlsxHtml(html);
          setStatus({ state: "ready", message: "" });
        }
      } catch (err) {
        if (!cancelled) {
          setStatus({
            state: "error",
            message: err?.message || "Failed to render spreadsheet.",
          });
        }
      }
    };

    setXlsxHtml("");
    run();

    return () => {
      cancelled = true;
      try {
        controller.abort();
      } catch {}
    };
  }, [file, kind, effectiveUrl, xlsxBuffer]);

  // PDF: keep width responsive to the preview panel body (fit-to-width behavior).
  useEffect(() => {
    const pdfLike =
      kind === "pdf" ||
      ((kind === "ppt" || kind === "pptx") && Boolean(pptPreviewPdfUrl));
    if (!file || !pdfLike) return;
    const el = pdfContainerRef.current;
    if (!el) return;

    let raf = null;
    const update = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        try {
          const next = Math.max(0, Math.floor(el.clientWidth || 0));
          setPdfWidth((prev) => (prev === next ? prev : next));
        } catch {}
      });
    };

    update();

    let ro = null;
    try {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
    } catch {
      ro = null;
      window.addEventListener("resize", update);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", update);
    };
  }, [file, kind, pptPreviewPdfUrl]);

  if (!file) return null;

  // Office viewer first (DOC/DOCX/PPT/PPTX/XLS/XLSX), then fall back to parser/empty state.
  const officeEligible =
    Boolean(officeType) &&
    officeUrlAnalysis.compatible &&
    Boolean(officeIframeSrc) &&
    !officeFailed &&
    (officePreflight.state === "idle" || officePreflight.state === "checking" || officePreflight.state === "ok");

  // For DOCX/XLSX, prefer parser fallback over Office viewer unless we're confident.
  const preferParser =
    (kind === "docx" || kind === "xlsx") && officeUrlAnalysis.isSameOrigin;

  if (
    officeEligible &&
    (!preferParser || officePreflight.state === "ok")
  ) {
    try {
      // eslint-disable-next-line no-console
      console.log("[OfficePreview] attempting office viewer", {
        file: file?.name,
        url: officeUrlAnalysis.absolute || effectiveUrl,
      });
    } catch {}
    return (
      <div className="file-preview__office">
        <OfficeViewerFrame
          key={`${officeType}:${officeUrlAnalysis.absolute}`}
          iframeSrc={officeIframeSrc}
          fileUrl={officeUrlAnalysis.absolute}
          officeType={officeType}
          onFail={(reason) => {
            setOfficeFailed(true);
            setOfficeFailReason(String(reason || "unknown"));
          }}
        />
      </div>
    );
  }

  // PPT/PPTX fallback: if Office viewer is skipped/failed, show generated PDF preview (if available).
  if ((kind === "ppt" || kind === "pptx") && pptPreviewPdfUrl) {
    debugLog("[OfficePreview] fallback → pdf_preview", {
      officeType,
      inferredKind: kind,
      reason: officeFailed
        ? officeFailReason || "office_viewer_failed"
        : officeUrlAnalysis.reason || "office_viewer_skipped",
      pdfUrl: pptPreviewPdfUrl,
    });
    // Reuse the existing PDF preview behavior for PPT preview-PDF.
    const urlForMedia = pptPreviewPdfUrl;
    const urlText = String(urlForMedia || "");
    const origin =
      typeof window !== "undefined" && window.location ? window.location.origin : "";
    const preferNative =
      typeof urlForMedia === "string" &&
      (urlText.startsWith("blob:") ||
        urlText.startsWith("/") ||
        urlText.includes("/uploads/") ||
        (origin && urlText.startsWith(origin)));

    const effectiveWidth =
      Number.isFinite(Number(pdfWidth)) && Number(pdfWidth) > 0
        ? Math.floor(
            Math.min(Number(pdfWidth), Number(maxPdfWidth) || Number(pdfWidth)),
          )
        : null;

    if (preferNative && !pdfNativeFailed) {
      return (
        <div className="file-preview__native-pdf">
          <NativePdfFrame
            key={String(urlForMedia)}
            src={withPdfViewerHints(String(urlForMedia))}
            onFail={() => setPdfNativeFailed(true)}
          />
        </div>
      );
    }

    return (
      <div className="file-preview__pdf" ref={pdfContainerRef}>
        <Document
          file={urlForMedia}
          onLoadSuccess={({ numPages: n }) => {
            setStatus({ state: "ready", message: "" });
            setNumPages(n);
          }}
          onLoadError={(err) => {
            setStatus({
              state: "error",
              message: err?.message || "Failed to load PDF preview.",
            });
          }}
          loading={<div className="file-preview__loader">Loading PDF...</div>}
          error={
            <div className="file-preview__empty">
              {status.message || "Failed to load PDF preview."}
            </div>
          }
        >
          {!effectiveWidth ? (
            <div className="file-preview__loader">Preparing preview...</div>
          ) : (
            Array.from(new Array(numPages || 0), (_, i) => (
              <Page
                key={i}
                pageNumber={i + 1}
                width={effectiveWidth}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                loading={<div className="file-preview__loader">Rendering...</div>}
              />
            ))
          )}
        </Document>
      </div>
    );
  }

  const urlForMedia = file.blob ? blobUrl : effectiveUrl;

  if (kind === "image" && urlForMedia) {
    return (
      <div className="file-preview__center">
        <img src={urlForMedia} alt="preview" className="file-preview__image" />
      </div>
    );
  }

  if (kind === "pdf" && urlForMedia) {
    const urlText = String(urlForMedia || "");
    const origin =
      typeof window !== "undefined" && window.location ? window.location.origin : "";
    const preferNative =
      typeof urlForMedia === "string" &&
      (urlText.startsWith("blob:") ||
        urlText.startsWith("/") ||
        urlText.includes("/uploads/") ||
        (origin && urlText.startsWith(origin)));

    const effectiveWidth =
      Number.isFinite(Number(pdfWidth)) && Number(pdfWidth) > 0
        ? Math.floor(Math.min(Number(pdfWidth), Number(maxPdfWidth) || Number(pdfWidth)))
        : null;

    // Native-first: use the browser PDF viewer in an iframe when possible.
    // Fallback: react-pdf/pdf.js canvas rendering.
    if (preferNative && !pdfNativeFailed) {
      return (
        <div className="file-preview__native-pdf">
          <NativePdfFrame
            key={String(urlForMedia)}
            src={withPdfViewerHints(String(urlForMedia))}
            onFail={() => setPdfNativeFailed(true)}
          />
        </div>
      );
    }

    return (
      <div className="file-preview__pdf" ref={pdfContainerRef}>
        <Document
          file={urlForMedia}
          onLoadSuccess={({ numPages: n }) => {
            setStatus({ state: "ready", message: "" });
            setNumPages(n);
          }}
          onLoadError={(err) => {
            setStatus({
              state: "error",
              message: err?.message || "Failed to load PDF preview.",
            });
          }}
          loading={<div className="file-preview__loader">Loading PDF...</div>}
          error={
            <div className="file-preview__empty">
              {status.message || "Failed to load PDF preview."}
            </div>
          }
        >
          {!effectiveWidth ? (
            <div className="file-preview__loader">Preparing preview...</div>
          ) : (
            Array.from(new Array(numPages || 0), (_, i) => (
              <Page
                key={i}
                pageNumber={i + 1}
                width={effectiveWidth}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                loading={<div className="file-preview__loader">Rendering...</div>}
              />
            ))
          )}
        </Document>
      </div>
    );
  }

  if (kind === "docx") {
    return (
      <div className="file-preview__docx">
        {status.state === "loading" ? (
          <div className="file-preview__loader">{status.message}</div>
        ) : null}
        {status.state === "error" ? (
          <div className="file-preview__empty">{status.message}</div>
        ) : null}
        <div ref={docxRef} className="file-preview__docx-inner" />
      </div>
    );
  }

  if (kind === "xlsx") {
    if (status.state === "loading") {
      return <div className="file-preview__loader">{status.message}</div>;
    }
    if (status.state === "error") {
      return <div className="file-preview__empty">{status.message}</div>;
    }
    return (
      <div
        className="file-preview__sheet"
        dangerouslySetInnerHTML={{ __html: xlsxHtml }}
      />
    );
  }

  if (
    (kind === "doc" || kind === "xls" || kind === "ppt" || kind === "pptx") &&
    officeType
  ) {
    const reason = officeFailed
      ? officeFailReason || "office_viewer_failed"
      : officeUrlAnalysis.reason || "office_viewer_skipped";
    // eslint-disable-next-line no-console
    console.info("[OfficePreview] fallback", {
      officeType,
      inferredKind: kind,
      reason,
      url: effectiveUrl,
    });
    return (
      <div className="file-preview__empty">
        Office preview unavailable ({reason}). Use Download to view this file.
      </div>
    );
  }

  if (kind === "text" && effectiveUrl) {
    return (
      <iframe
        src={effectiveUrl}
        title="text-preview"
        className="file-preview__text"
      />
    );
  }

  if (kind === "audio" && urlForMedia) {
    return (
      <div className="file-preview__audio">
        <audio controls src={urlForMedia} style={{ width: "100%" }} />
      </div>
    );
  }

  if (kind === "video" && urlForMedia) {
    return (
      <div className="file-preview__video">
        <video controls src={urlForMedia} className="file-preview__video-player" />
      </div>
    );
  }

  // PPT/PPTX and unknown: no safe in-app preview currently.
  return (
    <div className="file-preview__empty">
      No preview available for this file type.
    </div>
  );
};

const OfficeViewerFrame = ({ iframeSrc, fileUrl, officeType, onFail }) => {
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);
  const doneRef = useRef(false);
  const [loaded, setLoaded] = useState(false);

  const failOnce = (reason) => {
    if (doneRef.current) return;
    doneRef.current = true;
    try {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    } catch {}
    // eslint-disable-next-line no-console
    console.warn("[OfficePreview] failed", { officeType, reason, fileUrl });
    try {
      // eslint-disable-next-line no-console
      console.log("[OfficePreview] office viewer failed", reason);
    } catch {}
    onFail?.(reason);
  };

  useEffect(() => {
    doneRef.current = false;
    setLoaded(false);
    debugLog("[OfficePreview] iframe mount", { officeType, fileUrl, iframeSrc });

    try {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    } catch {}

    // If Office viewer doesn't load, fall back (commonly CSP/frame-src, unreachable URL, or Office-side fetch failure).
    timeoutRef.current = setTimeout(() => failOnce("timeout"), 12000);

    return () => {
      try {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeSrc, fileUrl, officeType]);

  const onLoad = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    try {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    } catch {}
    setLoaded(true);
    debugLog("[OfficePreview] iframe loaded", { officeType, fileUrl });
    try {
      // eslint-disable-next-line no-console
      console.log("[OfficePreview] office viewer loaded");
    } catch {}
  };

  const onError = () => failOnce("error");

  return (
    <>
      {!loaded ? (
        <div className="file-preview__loader" style={{ margin: 0 }}>
          Loading Office preview...
        </div>
      ) : null}
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title="Office preview"
        style={{
          width: "100%",
          height: "100%",
          border: 0,
          display: loaded ? "block" : "none",
        }}
        onLoad={onLoad}
        onError={onError}
      />
    </>
  );
};

const NativePdfFrame = ({ src, onFail }) => {
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);
  const loadedRef = useRef(false);
  const failedRef = useRef(false);

  const failOnce = () => {
    if (failedRef.current) return;
    failedRef.current = true;
    try {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    } catch {}
    onFail?.();
  };

  useEffect(() => {
    loadedRef.current = false;
    failedRef.current = false;

    try {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    } catch {}

    // If the iframe doesn't load, fall back to react-pdf.
    timeoutRef.current = setTimeout(() => {
      if (!loadedRef.current) failOnce();
    }, 8000);

    return () => {
      try {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const onLoad = () => {
    loadedRef.current = true;
    try {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    } catch {}

    // Best-effort detection for same-origin HTML error pages.
    // If we can access the document and it is HTML (likely auth/error page), fall back.
    try {
      const doc = iframeRef.current?.contentDocument;
      const ct = String(doc?.contentType || "").toLowerCase();
      if (ct && ct.includes("text/html")) {
        failOnce();
      }
    } catch {
      // cross-origin or PDF viewer; assume OK
    }
  };

  return (
    <iframe
      ref={iframeRef}
      className="file-preview__native-pdf-frame"
      src={src}
      title="PDF preview"
      onLoad={onLoad}
    />
  );
};

export default FilePreviewContent;
